import React from 'react';
import { derivePositioningRows } from './IpPositioningTable';

// Word-style "Asset Class Overview": the same rows and classification as the
// positioning dot table (the Portfolio Dashboard's SAA positioning logic),
// rendered as a coloured word per asset class instead of dots.
const WORDS = {
  strongUW: 'Strong Underweight',
  underweight: 'Underweight',
  neutral: 'Neutral',
  overweight: 'Overweight',
  strongOW: 'Strong Overweight',
};

const WORD_COLOURS = {
  strongUW: '#8B0000',
  underweight: '#E53935',
  neutral: '#f0a500',
  overweight: '#43A047',
  strongOW: '#2d8a4e',
};

// Word-report display order (deliberately different from the dot table's
// dashboard order): Equities, Bonds, Credit, Infrastructure, Property.
const ACO_ORDER = ['Equities', 'Government Bonds', 'Corporate Debt', 'Infrastructure', 'Real Estate'];

// From the September-2026 edition (investment team decision, Oct 2026) the
// Overview shows Australian and Global Equities on separate lines — exactly the
// dot table's rows and positions (EM folded into Global) — and covers only these
// classes; anything else (Cash, Commodities, Global Alpha...) is left out.
// Earlier editions keep the combined-Equities layout so published months
// re-render as they did (the table is drawn client-side from snapshot data).
const SPLIT_FROM = '2026-09';
const ACO_ROWS_SPLIT = ['Australian Equities', 'International Equities', 'Government Bonds',
  'Corporate Debt', 'Infrastructure', 'Real Estate'];
// `period` is the report month ('YYYY-MM'); a missing period gets the current layout.
const isSplit = (period) => !(typeof period === 'string' && period < SPLIT_FROM);

export default function IpAssetClassOverview({ data, period }) {
  // Tailored client with no positioning suite chosen (Manage Positioning).
  if (data?.unset) return <div className="chart-placeholder">{data.message}</div>;
  const rows = isSplit(period)
    ? (derivePositioningRows(data)?.rows || [])
      .filter((r) => ACO_ROWS_SPLIT.includes(r.ac))
      .sort((a, b) => ACO_ROWS_SPLIT.indexOf(a.ac) - ACO_ROWS_SPLIT.indexOf(b.ac))
    : (derivePositioningRows(data, { combineEquities: true })?.rows || [])
      .filter((r) => r.ac !== 'Cash')
      .sort((a, b) => ((ACO_ORDER.indexOf(a.ac) + 1 || 99) - (ACO_ORDER.indexOf(b.ac) + 1 || 99)));
  if (rows.length === 0) {
    return <div className="chart-placeholder">No positioning data available</div>;
  }

  return (
    <table className="ip-aco-table">
      <tbody>
        {rows.map((r) => (
          <tr key={r.ac}>
            <td className="ip-aco-label">{r.display}</td>
            <td className="ip-aco-word" style={{ color: WORD_COLOURS[r.position] }}>{WORDS[r.position]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
